import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@lpc/brand-kit';
import { useCart } from '@/lib/cart-store';
import { useCartPricing } from './use-cart-pricing';
/**
 * useReactToStockEvents
 *
 * Mounted once at the cart-UI root (Layout.tsx). Subscribes to the shared
 * `cart-pricing` query (driven by SSE invalidation in catalog-events.ts).
 * When any line surfaces an `availabilityError`, this hook auto-clamps the
 * cart line to `availableUnits` (or removes it when sold out) and emits a
 * single deduped toast per affected product per stock event.
 *
 * Why a single mount point: avoids duplicate toasts when multiple cart
 * surfaces (CartPage + SideCartPanel + MobileCartBar) read the same data.
 * The hook drives the mutations; the visible cart UIs just render state.
 */
export function useReactToStockEvents() {
    const { t, i18n } = useTranslation('cart');
    const toast = useToast();
    const { items, setQuantity, remove } = useCart();
    const { data } = useCartPricing();
    const locale = i18n.language.startsWith('fr') ? 'fr' : 'en';
    // Track which (productProfileId, availableUnits) combos we already reacted
    // to so we don't loop or spam toasts when the same response re-fires.
    const reactedRef = useRef(new Map());
    useEffect(() => {
        if (!data)
            return;
        for (const line of data.lines) {
            if (!line.availabilityError) {
                reactedRef.current.delete(line.productProfileId);
                continue;
            }
            const available = line.availabilityError.availableUnits;
            const lastReactedAvailable = reactedRef.current.get(line.productProfileId);
            if (lastReactedAvailable === available)
                continue;
            reactedRef.current.set(line.productProfileId, available);
            const item = items.find((i) => i.productProfileId === line.productProfileId);
            if (!item)
                continue;
            const name = line.displayName?.[locale] ??
                item.displayName?.[locale] ??
                line.publicSlug;
            if (available <= 0) {
                remove(line.productProfileId);
                toast.show(t('availability.removed', { name }), 'error');
            }
            else if (item.quantity > available) {
                setQuantity(line.productProfileId, available);
                toast.show(t('availability.clamped', { name, count: available }), 'info');
            }
        }
    }, [data, items, setQuantity, remove, toast, t, locale]);
}
